/**
 * Datas relativas de filtro — o token `AGORA` e seus deslocamentos.
 *
 * Um filtro de rotina escrito com data fixa envelhece no dia seguinte: "criado
 * a partir de 2026-09-18" deixa de significar "últimos 7 dias" já na próxima
 * execução. O backend aceita `AGORA[-Nm|-Nh|-Nd]` e resolve o valor **na hora
 * do disparo**, não no cadastro, e é isso que faz uma rotina diária ter sentido.
 *
 * O contrato é o mesmo do `validarDataFiltro` do backend: `AGORA` sozinho,
 * ou `AGORA-` mais um inteiro de 0 a 3650 seguido de `m`, `h` ou `d`. Só para
 * trás — não existe `AGORA+`. Espelhamos aqui para que o erro apareça no campo
 * em vez de voltar como 400.
 */

export const UNIDADES_RELATIVAS = ["m", "h", "d"] as const;
export type UnidadeRelativa = (typeof UNIDADES_RELATIVAS)[number];

export const UNIDADE_RELATIVA_LABEL: Record<UnidadeRelativa, string> = {
  m: "minutos",
  h: "horas",
  d: "dias",
};

const UNIDADE_RELATIVA_SINGULAR: Record<UnidadeRelativa, string> = {
  m: "minuto",
  h: "hora",
  d: "dia",
};

export const MAX_OFFSET_RELATIVO = 3650;

const MS_POR_UNIDADE: Record<UnidadeRelativa, number> = {
  m: 60 * 1000,
  h: 60 * 60 * 1000,
  d: 24 * 60 * 60 * 1000,
};

const DATA_RELATIVA = /^AGORA(?:-(\d+)(m|h|d))?$/;

export const TOKEN_AGORA = "AGORA";

export const ehDataRelativa = (valor: string): boolean => DATA_RELATIVA.test(valor.trim());

/** `AGORA-7d` → `{ quantidade: 7, unidade: 'd' }`; `AGORA` → quantidade 0. */
export function partesDataRelativa(
  valor: string,
): { quantidade: number; unidade: UnidadeRelativa } | null {
  const m = DATA_RELATIVA.exec(valor.trim());
  if (!m) return null;
  return {
    quantidade: Number(m[1] ?? 0),
    unidade: (m[2] as UnidadeRelativa) ?? "d",
  };
}

/** Quantidade zero volta a ser `AGORA` puro — é o mesmo instante. */
export const montarDataRelativa = (quantidade: number, unidade: UnidadeRelativa): string =>
  quantidade > 0 ? `${TOKEN_AGORA}-${quantidade}${unidade}` : TOKEN_AGORA;

/**
 * Instante que o valor representa, em milissegundos. Serve para comparar as
 * duas pontas de um período sem repetir a conta: um valor relativo e um
 * absoluto podem ser comparados entre si depois de resolvidos.
 *
 * `null` quando o valor não é data reconhecível — quem chama decide se isso é
 * erro ou campo ainda em branco.
 */
export function instanteDeValorData(valor: string, agora = new Date()): number | null {
  const v = valor.trim();
  if (!v) return null;

  const relativa = partesDataRelativa(v);
  if (relativa) {
    return agora.getTime() - relativa.quantidade * MS_POR_UNIDADE[relativa.unidade];
  }

  const instante = Date.parse(v);
  return Number.isFinite(instante) ? instante : null;
}

/** Rótulo curto para a pill do filtro: `AGORA-7d` → "7 dias atrás". */
export function descreverDataRelativa(valor: string): string {
  const partes = partesDataRelativa(valor);
  if (!partes) return valor;
  if (partes.quantidade === 0) return "agora";
  const unidade =
    partes.quantidade === 1
      ? UNIDADE_RELATIVA_SINGULAR[partes.unidade]
      : UNIDADE_RELATIVA_LABEL[partes.unidade];
  return `${partes.quantidade} ${unidade} atrás`;
}

/** Data que o token resolve neste instante, para conferência no cadastro. */
export function previewDataRelativa(valor: string, agora = new Date()): string {
  const instante = instanteDeValorData(valor, agora);
  if (instante === null) return "";
  return new Date(instante).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}
